// src/pages/InstagramCallbackPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';

export function InstagramCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState('');

  const connectInstagram = useCallback(async (code: string, state: string) => {
    try {
      await api.connectInstagram(code, state);
      localStorage.removeItem('instagram_oauth_state');
      setStatus('success');
      setTimeout(() => {
        navigate('/dashboard?tab=settings');
      }, 2000);
    } catch (err: any) {
      setStatus('error');
      // Show user-friendly error messages
      const errorMessage = err.response?.data?.error || err.message || 'Failed to connect Instagram account';

      if (err.response?.data?.requiresBusinessAccount) {
        setError('Instagram Business Account required. Please convert your account to a Business Account in Instagram Settings > Account Type.');
      } else if (errorMessage.includes('Business Account')) {
        setError('This feature requires an Instagram Business Account. Please convert your account in Instagram Settings.');
      } else {
        setError(errorMessage);
      }
    }
  }, [navigate]);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const savedState = localStorage.getItem('instagram_oauth_state');

    if (!code) {
      setStatus('error');
      setError('Missing authorization code');
      return;
    }

    if (!state || state !== savedState) {
      setStatus('error');
      setError('Invalid state parameter');
      return;
    }

    connectInstagram(code, state);
  }, [searchParams, connectInstagram]);

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Connecting your Instagram account...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">✕</div>
          <h2 className="text-2xl font-bold mb-2">Connection Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard?tab=settings')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-green-600 text-6xl mb-4">✓</div>
        <h2 className="text-2xl font-bold mb-2">Connected Successfully!</h2>
        <p className="text-gray-600">Redirecting to dashboard...</p>
      </div>
    </div>
  );
}
